// import swiper styles
import 'swiper/css';

import { useState } from 'react';
import { useSwiper } from 'swiper/react';

import { useDetectUserTouch, useIsSmallerThan } from '@/components/utils';
import ArrowThinLeft from '@/icons/small/Arrow-Thin-Left.svg';
import ArrowThinRight from '@/icons/small/Arrow-Thin-Right.svg';

import { type IIntroProps, Intro } from '../Intro';
import { type ILocationTeaserProps, LocationTeaser } from '../LocationTeaser';
import {
  StyledGrid,
  StyledLocationTeaser,
  StyledSlider,
  StyledSwiper,
  StyledSwiperButtonNext,
  StyledSwiperButtonPrev,
  StyledSwiperSlide,
  StyledWrapper,
} from './LocationsPreview.styled';

interface ISwiperButtonProps {
  direction: 'prev' | 'next';
  label?: string;
  swiperIndex: number;
}

const SwiperButton = ({ direction, label }: ISwiperButtonProps) => {
  const swiper = useSwiper();

  const Buttons = {
    prev: (
      <StyledSwiperButtonPrev
        label={label}
        icon={<ArrowThinLeft />}
        disabled={swiper?.isBeginning}
        onClick={() => swiper?.slidePrev()}
      />
    ),
    next: (
      <StyledSwiperButtonNext
        label={label}
        icon={<ArrowThinRight />}
        disabled={swiper?.isEnd}
        onClick={() => swiper?.slideNext()}
      />
    ),
  } as const;

  return Buttons[direction];
};

interface ILocationsPreviewProps {
  className?: string;
  intro: Pick<IIntroProps, 'subtitle' | 'headline' | 'copy'>;
  items: (Omit<ILocationTeaserProps, 'className'> & {
    id: string;
  })[];
  sliderControls?: {
    prevLabel: string;
    nextLabel: string;
  };
}

export const LocationsPreview = ({
  className,
  intro,
  items,
  sliderControls,
}: ILocationsPreviewProps) => {
  const [swiperIndex, setSwiperIndex] = useState<number>(0);

  const isSmallerThanTabletLandscape = useIsSmallerThan('TABLET_LANDSCAPE');
  const isSmallerThanTabletPortrait = useIsSmallerThan('TABLET_PORTRAIT');

  const isTouch = useDetectUserTouch();

  return (
    <section className={className}>
      <StyledWrapper
        forwardedAs="div"
        width={{ laptop: 'm', tabletLandscape: 'm' }}
      >
        <Intro
          subtitle={intro.subtitle}
          headline={{
            label: intro.headline.label,
            size: 'h3',
            as: 'h2',
          }}
          copy={intro.copy}
          nested
        />
      </StyledWrapper>
      {isSmallerThanTabletLandscape ? (
        <StyledSlider>
          <StyledSwiper
            spaceBetween={16}
            slidesPerView={isSmallerThanTabletPortrait ? 1.25 : 2.25}
            onSlideChange={(swiper) => {
              setSwiperIndex(swiper.activeIndex);
            }}
            tag="div"
            wrapperTag="ul"
            initialSlide={0}
            slidesOffsetBefore={16}
            slidesOffsetAfter={16}
            centeredSlides={isSmallerThanTabletPortrait}
            centeredSlidesBounds={true}
            keyboard={true}
          >
            {items.map((item) => (
              <StyledSwiperSlide key={item.id} tag="li">
                <LocationTeaser
                  headline={item.headline}
                  subtitle={item.subtitle}
                  image={item.image}
                  link={item.link}
                />
              </StyledSwiperSlide>
            ))}
            {!isTouch && (
              <>
                <SwiperButton
                  label={sliderControls?.prevLabel}
                  direction="prev"
                  swiperIndex={swiperIndex}
                />
                <SwiperButton
                  label={sliderControls?.nextLabel}
                  direction="next"
                  swiperIndex={swiperIndex}
                />
              </>
            )}
          </StyledSwiper>
        </StyledSlider>
      ) : (
        <StyledGrid
          forwardedAs="div"
          width={{ laptop: 'l', tabletLandscape: 'm' }}
          $items={items.length}
        >
          {items.map((item) => (
            <StyledLocationTeaser
              headline={item.headline}
              subtitle={item.subtitle}
              image={item.image}
              link={item.link}
              key={item.id}
            />
          ))}
        </StyledGrid>
      )}
    </section>
  );
};
